import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Calendar, Package, Plane, Flag, Ban, Unlock, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ItemCard } from "../components/ItemCard";
import { BadgeList } from "../components/BadgeChip";

const lastSeenText = (iso) => {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 0) return null;
  if (mins < 1) return "Gerade online";
  if (mins < 60) return `Zuletzt online vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Zuletzt online vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Zuletzt online gestern";
  if (days < 7) return `Zuletzt online vor ${days} Tagen`;
  return "Zuletzt online vor über einer Woche";
};

export default function SellerProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);

  const load = () => {
    api.get(`/users/${userId}/profile`).then(({ data }) => setProfile(data)).catch(() => setProfile(false));
    api.get("/search", { params: { seller_id: userId, limit: 24 } }).then(({ data }) => setItems(data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const reportProfile = async () => {
    if (!window.confirm(`${profile.username} wirklich melden?`)) return;
    try {
      const { data } = await api.post("/reports", { entity_type: "user", entity_id: userId, reason: "UNANGEMESSEN", details: "Profil gemeldet" });
      toast.success(`Meldung ${data.number} eingereicht`, { description: "Unser Team prüft den Fall." });
    } catch (e) { toast.error(errText(e)); }
  };

  const toggleBlock = async () => {
    try {
      if (profile.blocked_by_me) {
        await api.delete(`/users/${userId}/block`);
        toast.success("Blockierung aufgehoben");
      } else {
        if (!window.confirm(`${profile.username} blockieren? Artikel, Chats und Bewertungen werden ausgeblendet.`)) return;
        const { data } = await api.post(`/users/${userId}/block`);
        toast.success(`${profile.username} blockiert`, { description: data.number ? `Meldung ${data.number} wurde automatisch erstellt.` : undefined });
      }
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const reportReview = async (r) => {
    if (!window.confirm("Diese Bewertung melden?")) return;
    try {
      const { data } = await api.post("/reports", { entity_type: "review", entity_id: r.id, reason: "UNANGEMESSEN", details: `Bewertung von ${r.author?.username || "Nutzer"}: „${(r.comment || "").slice(0, 120)}“` });
      toast.success(`Meldung ${data.number} eingereicht`);
    } catch (e) { toast.error(errText(e)); }
  };

  if (profile === null) return <div className="max-w-6xl mx-auto px-4 py-10"><div className="h-40 bg-muted rounded-2xl animate-pulse" /></div>;
  if (profile === false) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-muted-foreground">Profil nicht gefunden.</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center gap-5 border border-border rounded-2xl p-6 bg-card" data-testid="seller-profile-header">
        <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-bold">
          {profile.username?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl sm:text-4xl font-bold">{profile.username}</h1>
          <div className="mt-2" data-testid="profile-badges"><BadgeList badges={profile.badges} max={4} size="md" /></div>
          {profile.bio && <p className="text-sm text-muted-foreground mt-2">{profile.bio}</p>}
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5" data-testid="profile-rating">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              {profile.rating_avg ? `${profile.rating_avg} (${profile.rating_count} Bewertungen)` : "Noch keine Bewertungen"}
            </span>
            <span className="flex items-center gap-1.5"><Package className="w-4 h-4" /> {profile.sales_count} Verkäufe</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Dabei seit {formatDate(profile.created_at)}</span>
            {lastSeenText(profile.last_seen_at) && (
              <span className="flex items-center gap-1.5" data-testid="profile-last-seen">
                <Clock className="w-4 h-4" /> {lastSeenText(profile.last_seen_at)}
              </span>
            )}
            {profile.response_rate != null && <span>Antwortquote {Math.round(profile.response_rate * 100)} %</span>}
            {profile.avg_response_minutes != null && <span>Ø Antwortzeit {profile.avg_response_minutes < 60 ? `${profile.avg_response_minutes} Min.` : `${Math.round(profile.avg_response_minutes / 60)} Std.`}</span>}
            {profile.avg_ship_hours != null && <span>Ø Versand {Math.round(profile.avg_ship_hours)} Std.</span>}
          </div>
        </div>
        {user && user.id !== userId && (
          <div className="flex flex-col gap-2 self-start">
            <button data-testid="profile-block-btn" onClick={toggleBlock}
              className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3.5 py-2 border ${profile.blocked_by_me ? "border-green-300 text-green-700 hover:bg-green-50" : "border-border text-muted-foreground hover:text-destructive hover:border-destructive/40"}`}>
              {profile.blocked_by_me ? <><Unlock className="w-3.5 h-3.5" /> Blockierung aufheben</> : <><Ban className="w-3.5 h-3.5" /> Blockieren</>}
            </button>
            <button data-testid="profile-report-btn" onClick={reportProfile}
              className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3.5 py-2 border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40">
              <Flag className="w-3.5 h-3.5" /> Profil melden
            </button>
          </div>
        )}
      </div>

      {profile.blocked_by_me && (
        <div data-testid="blocked-banner" className="mt-4 border border-red-200 bg-red-50 rounded-2xl p-4 text-sm font-medium text-red-700 flex items-center gap-2.5">
          <Ban className="w-4 h-4 shrink-0" /> Du hast diesen Nutzer blockiert. Seine Artikel, Chats und Bewertungen werden dir nicht mehr angezeigt.
        </div>
      )}

      {profile.vacation_mode && (
        <div data-testid="vacation-banner" className="mt-4 border border-amber-300 bg-amber-50 rounded-2xl p-4 text-sm font-medium text-amber-800 flex items-center gap-2.5">
          <Plane className="w-4 h-4 shrink-0" /> Dieser Verkäufer ist gerade im Urlaub. Die Artikel sind vorübergehend pausiert.
        </div>
      )}

      <h2 className="text-lg font-bold mt-8 mb-4">Artikel ({items.length})</h2>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Artikel.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {items.map((it, i) => <ItemCard key={it.id} item={it} index={i} />)}
        </div>
      )}

      {profile.reviews?.length > 0 && (
        <>
          <h2 className="text-lg font-bold mt-10 mb-4">Bewertungen</h2>
          <div className="space-y-3">
            {profile.reviews.map((r) => (
              <div key={r.id} className="border border-border rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{r.author?.username || "Nutzer"}</span>
                  <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-muted"}`} />)}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{formatDate(r.created_at)}</span>
                  {user && user.id !== r.author_id && (
                    <button data-testid={`report-review-${r.id}`} onClick={() => reportReview(r)} title="Bewertung melden"
                      className="p-1.5 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                      <Flag className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {r.comment && <p className="text-sm mt-2">{r.comment}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
